import { GroupPostCreationWizard } from '../../containers'
import { GroupScreen } from './group-screen'
import { createParam } from 'solito'
import { useState } from 'react'
import { useAuth } from 'app/provider/auth'
import {
  hasJoinedGroup,
  canInteract,
  canAccessPosts,
  hasPendingGroupMembershipRequest,
} from '../../helpers'
import { groupPostsQuery, useGroupPostsQuery } from './queries'
import {
  useGroupQuery,
  useUserGroupJoinRequestsQuery,
  useUserGroupsQuery,
  userGroupJoinRequestsQuery,
  userGroupsQuery,
} from '../../queries'
import { GroupAnnouncement, GroupEvent, GroupPost } from 'app/types'
import { GroupAd } from 'app/features/ads/blocks'
import { mergeArraysAtIntervals } from 'app/lib/merge-arrays-at-intervals'

const { useParam } = createParam<{ id: string }>()

export type GroupPostOrAd =
  | { type: 'groupPost'; data: GroupPost }
  | { type: 'ad'; data: GroupAd }
  | { type: 'groupEvent'; data: GroupEvent }
  | { type: 'groupAnnouncement'; data: GroupAnnouncement }

export interface GroupScreenContainerProps {
  HeaderComponent?: React.ReactNode
}

export const GroupScreenContainer = (props: GroupScreenContainerProps) => {
  const [id] = useParam('id')
  const { user } = useAuth()

  // Pull to refresh
  const [groupRefreshing, setGroupRefreshing] = useState(false)
  const [createPostModalOpen, setCreatePostModalOpen] = useState(false)

  const {
    data: groupData,
    refetch: refetchGroup,
    networkStatus: groupNetworkStatus,
  } = useGroupQuery({
    variables: { id: id || '' },
    notifyOnNetworkStatusChange: true,
  })

  const group = groupData?.Group

  const { data: userGroups, refetch: refetchUserGroups } = useUserGroupsQuery({
    variables: {
      ...userGroupsQuery.defaultVariables,
    },
    skip: !user,
    fetchPolicy: 'cache-and-network',
  })

  const { data: userGroupJoinRequestsData } = useUserGroupJoinRequestsQuery({
    variables: {
      ...userGroupJoinRequestsQuery.defaultVariables,
    },
    fetchPolicy: 'cache-and-network',
    skip: !user,
  })

  const { isMember, isAdmin } = hasJoinedGroup({
    userGroups: userGroups?.userGroups?.docs,
    groupId: group?.id || '',
  })

  const pendingRequest = hasPendingGroupMembershipRequest({
    groupJoinRequests: userGroupJoinRequestsData?.userGroupJoinRequests?.docs || [],
    groupId: group?.id || '',
  })

  const canReadPosts = canAccessPosts({
    group,
    user,
    isMember,
  })

  const {
    data: groupPostsData,
    networkStatus,
    refetch: refetchGroupPosts,
    fetchMore,
  } = useGroupPostsQuery(
    {
      ...groupPostsQuery.defaultVariables,
      id: group?.id || '',
    },
    {
      skip: !canReadPosts,
      notifyOnNetworkStatusChange: true,
    }
  )

  const groupPosts = groupPostsData?.searchGroupPosts

  const formattedGroupPosts = groupPostsQuery.formatFn(groupPostsData).map((item) => {
    if (item.type === 'groupAnnouncement') {
      return {
        type: 'groupAnnouncement' as const,
        data: item,
      }
    }

    if (item.type === 'groupEvent')
      return {
        type: 'groupEvent' as const,
        data: item,
      }

    return {
      type: 'groupPost' as const,
      data: item,
    }
  })

  const formattedAds =
    groupPosts?.ads.map((ad) => ({
      type: 'ad' as const,
      data: ad,
    })) || []

  const groupPostsWithAds = mergeArraysAtIntervals<GroupPostOrAd>(
    formattedGroupPosts,
    formattedAds,
    5,
    5
  )
  const hasMore = (groupPosts?.totalDocs || 0) > formattedGroupPosts.length

  const handleFetchMore = async () => {
    if (!hasMore || !groupPosts) return
    await fetchMore({
      variables: {
        page: groupPosts.page + 1,
      },
    })
  }

  const handlePullToRefresh = async () => {
    setGroupRefreshing(true)
    await Promise.all([refetchGroup(), refetchGroupPosts()])
    setGroupRefreshing(false)
  }

  const groupLoading = groupNetworkStatus === 1
  const groupPostsLoading = networkStatus === 1
  const groupPostsFetching = networkStatus === 4 || networkStatus === 2
  const groupPostsFetchingMore = networkStatus === 3

  // No results if not loading and can read posts
  const noResults =
    groupPosts?.docs?.length === 0 && !groupPostsLoading && !groupPostsFetching && canReadPosts

  return (
    <>
      <GroupScreen
        HeaderComponent={props.HeaderComponent}
        group={group}
        groupLoading={groupLoading}
        groupPosts={groupPostsWithAds}
        totalResults={groupPosts?.totalDocs || 0}
        noResults={noResults}
        groupPostsLoading={groupPostsLoading}
        groupPostsFetching={groupPostsFetching}
        groupPostsFetchingMore={groupPostsFetchingMore}
        refetchGroupPosts={refetchGroupPosts}
        hasMore={hasMore}
        fetchMore={handleFetchMore}
        refetchUserGroups={refetchUserGroups}
        onCreatePostOpenChange={setCreatePostModalOpen}
        isMember={isMember}
        refreshing={groupRefreshing}
        canInteract={isMember && canInteract({ user, group })}
        onPullToRefresh={handlePullToRefresh}
        isAdmin={isAdmin}
        pendingRequest={pendingRequest}
      />
      {user && group && isMember && (
        <GroupPostCreationWizard
          user={user}
          open={createPostModalOpen}
          onOpenChange={setCreatePostModalOpen}
          defaultGroup={{
            id: group.id,
            name: group.name,
          }}
        />
      )}
    </>
  )
}
